import { Center } from "@mantine/core";
import { isRouteErrorResponse, useLocation, useRouteError } from "react-router";
import {
  configureErrorReporting,
  createHttpSink,
  reportError,
  type ErrorSurface,
} from "@how/observability";
import { EmptyState, ErrorState } from "@how/ui";
import type { AppSupabaseClient } from "./supabase";

export interface AppErrorReportingOptions {
  readonly surface: ErrorSurface;
  readonly supabaseUrl: string;
  readonly client: AppSupabaseClient;
  /** Which build this is, when the deploy sets one; "unknown" otherwise. */
  readonly release?: string;
}

let listening = false;

/**
 * Wires this app's one error destination (ADR 0038): the report-error Edge Function. Called once, where the app wires
 * its providers. The session token goes with each report so the function can tell who was signed in; the report
 * itself never names a church or a person. Errors outside React (an unhandled promise, a script error) are reported
 * too, without a screen, because nobody is looking at one.
 */
export function configureAppErrorReporting(options: AppErrorReportingOptions): void {
  configureErrorReporting({
    surface: options.surface,
    release: options.release ?? "unknown",
    sink: createHttpSink(
      `${options.supabaseUrl.replace(/\/$/, "")}/functions/v1/report-error`,
      async () => {
        const { data } = await options.client.auth.getSession();
        return data.session?.access_token ?? null;
      },
    ),
  });
  if (listening || typeof window === "undefined") return;
  listening = true;
  window.addEventListener("error", (event) => {
    reportError(event.error ?? event.message, { route: window.location.pathname });
  });
  window.addEventListener("unhandledrejection", (event) => {
    reportError(event.reason, { route: window.location.pathname });
  });
}

/*
 * One report per error. React renders an error page twice in development (StrictMode), and a route can re-render;
 * neither should file a second report or show a second reference.
 */
const reported = new WeakMap<object, string>();

function referenceFor(error: unknown, route: string): string {
  if (typeof error === "object" && error !== null) {
    const known = reported.get(error);
    if (known !== undefined) return known;
    const reference = reportError(error, { route });
    reported.set(error, reference);
    return reference;
  }
  return reportError(error, { route });
}

/**
 * The page a route shows when it fails: a plain explanation and the reference to quote to support (support.md).
 * A page that does not exist is not an error and is not reported. Used as each app router's root errorElement.
 */
export function RouteErrorPage() {
  const error = useRouteError();
  const location = useLocation();
  if (isRouteErrorResponse(error) && error.status === 404) {
    return (
      <Center mih="60vh">
        <EmptyState title="Page not found" description="There is nothing at this address." />
      </Center>
    );
  }
  const reference = referenceFor(error, location.pathname);
  return (
    <Center mih="60vh" px="md">
      <ErrorState
        title="Something went wrong"
        message="This page could not be shown. If it keeps happening, contact support and quote this reference."
        reference={reference}
        onRetry={() => {
          window.location.reload();
        }}
      />
    </Center>
  );
}
